import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";

export type ExcuseRequestSummary = components["schemas"]["ExcuseRequestSummary"];
export type ExcuseRequestDetails = components["schemas"]["ExcuseRequestDetails"];
export type ExcuseRequestLesson = components["schemas"]["ExcuseRequestLesson"];
export type ExcuseRequestStatus = NonNullable<components["schemas"]["ExcuseRequestStatus"]>;

export const excuseStatuses: readonly ExcuseRequestStatus[] = ["Pending", "Approved", "Rejected"];

export const excuseStatusColor: Record<ExcuseRequestStatus, string> = {
  Pending: "gold",
  Approved: "green",
  Rejected: "red",
};

export const excuseKeys = {
  all: ["excuse-requests"] as const,
  list: (filters: object) => ["excuse-requests", "list", filters] as const,
  detail: (id: string) => ["excuse-requests", "detail", id] as const,
  pendingCount: ["excuse-requests", "pending-count"] as const,
};

const PENDING_REFRESH_MS = 60_000;

/** How many requests wait for a decision, for the menu badge; refreshed every minute and after every decision. */
export function usePendingExcuseCount(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: excuseKeys.pendingCount,
    enabled,
    refetchInterval: PENDING_REFRESH_MS,
    queryFn: async () => {
      const { data, error } = await api.GET("/attendance/excuse-requests", { params: { query: { Status: "Pending", Page: 1, PageSize: 1 } } });
      if (error || !data) throw new Error(t("app.error"));
      return data.totalCount;
    },
  });
}

/** One request with the absences it covers (GET /attendance/excuse-requests/{id}); attendance.excuse. */
export function useExcuseRequest(id: string | null) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: excuseKeys.detail(id ?? ""),
    enabled: id !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/attendance/excuse-requests/{id}", { params: { path: { id: id ?? "" } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}
